import React, { useEffect, useState } from 'react';
import JobDetailsModal from './JobDetailsModal';
import { useJobModal } from '../contexts/JobModalContext';
import { toast } from 'react-hot-toast';

/**
 * Global job modal component that renders once in the app
 * and is controlled by the JobModalContext
 */
const GlobalJobModal: React.FC = () => {
  const { currentJob, isModalOpen, closeJobModal, error } = useJobModal();
  const [localModalOpen, setLocalModalOpen] = useState(false);
  
  // Sync the local state with context state
  useEffect(() => {
    setLocalModalOpen(isModalOpen);
  }, [isModalOpen]);

  // Show error toast if job loading fails
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Add an effect to handle ESC key to close modal and clean URL
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      // Disable ESC key closing as per requirements
      // User should only close via the X button or application confirmation
      if (event.key === 'Escape') {
        // Prevent default browser behavior
        event.preventDefault();
      }
    };

    // Listen for ESC key when modal is open
    if (localModalOpen) {
      window.addEventListener('keydown', handleEscKey);
    }

    // Clean up listener
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [localModalOpen]);

  // Don't render anything if modal shouldn't be open
  if (!currentJob || !localModalOpen) {
    return null;
  }
  
  // Don't render main modal for demo jobs - let applicationTracker handle them
  if (currentJob._id?.startsWith('demo-') || currentJob.jobType === 'DEMO') {
    return null;
  }
  
  // Define explicit close handler
  const handleModalClose = () => {
    // Force local state closed first to ensure UI updates
    setLocalModalOpen(false);
    // Then call the context method
    closeJobModal();
  };

  return (
    <JobDetailsModal
      job={currentJob}
      isOpen={localModalOpen}
      onClose={handleModalClose}
    />
  );
};

export default GlobalJobModal; 